import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import flatpickr from "ns-flatpickr";
import { Thai } from "ns-flatpickr/dist/l10n/th.js";
import { english } from "ns-flatpickr/dist/l10n/default.js";
import { Instance } from "ns-flatpickr/dist/types/instance";
import { Locale } from "ns-flatpickr/dist/types/locale";
import { DateLimit, DateOption, Hook, Options, ParsedOptions } from "ns-flatpickr/dist/types/options";
import PGDElement, { Platforms } from "./pg.element";
import { loadPlugins } from "./plugin/PluginLoader";
import StyleLoader from "./styles/StyleLoader";
import { FlatpickrTheme } from "./styles/Themes";

@customElement("pg-datepicker")
export class PGDDatepicker extends LitElement implements PGDElement {
  platform(): string[] {
    return ["web", "mobile"];
  }
  isSupport(platform: Platforms): boolean {
    return this.platform().includes(platform);
  }

  @property({ type: String })
  placeholder = "";

  @property({ type: String })
  altFormat = "F j, Y";

  @property({ type: Boolean })
  altInput = false;

  @property({ type: String })
  altInputClass = "";

  @property({ type: Boolean })
  allowInput = false;

  @property({ type: String })
  ariaDateFormat = "F j, Y";

  @property({ type: Boolean })
  clickOpens = true;

  @property({ type: String })
  dateFormat = "d-m-Y";

  @property({ type: Object })
  defaultDate?: DateOption | DateOption[];

  @property({ type: Number })
  defaultHour = 12;

  @property({ type: Number })
  defaultMinute = 0;

  @property({ type: Array })
  disable: DateLimit<DateOption>[] = [];

  @property({ type: Boolean })
  disableMobile = false;

  @property({ type: Array })
  enable: DateLimit<DateOption>[] | undefined = undefined;

  @property({ type: Boolean })
  enableTime = false;

  @property({ type: Boolean })
  enableSeconds = false;

  @property({ type: Function })
  formatDateFn?: (date: Date, format: string, locale: Locale) => string;

  @property({ type: Number })
  hourIncrement = 1;

  @property({ type: Number })
  minuteIncrement = 5;

  @property({ type: Boolean })
  inline = false;

  @property({ type: String })
  maxDate?: DateOption;

  @property({ type: String })
  minDate?: DateOption;

  @property({ type: String })
  mode: "single" | "multiple" | "range" | "time" = "single";

  @property({ type: String })
  nextArrow = ">";

  @property({ type: String })
  prevArrow = "<";

  @property({ type: Boolean })
  noCalendar = false;

  @property({ type: Function })
  onChange?: Hook;

  @property({ type: Function })
  onClose?: Hook;

  @property({ type: Function })
  onOpen?: Hook;

  @property({ type: Function })
  onReady?: Hook;

  @property({ type: Function })
  onMonthChange?: Hook;

  @property({ type: Function })
  onYearChange?: Hook;

  @property({ type: Function })
  onValueUpdate?: Hook;

  @property({ type: Function })
  parseDateFn?: (date: string, format: string) => Date;

  @property({ type: String })
  position: "auto" | "above" | "below" = "auto";

  @property({ type: Boolean })
  shorthandCurrentMonth = false;

  @property({ type: Number })
  showMonths = 1;

  @property({ type: Boolean })
  static = false;

  @property({ type: Boolean })
  time_24hr = false;

  @property({ type: Boolean })
  weekNumbers = false;

  @property({ type: Boolean })
  wrap = false;

  @property({ type: String })
  theme = "confetti";

  @property({ type: Number })
  firstDayOfWeek = 1;

  @property({ type: String })
  locale: string | undefined;

  @property({ type: Boolean })
  localeTh = false;

  @property({ type: Boolean, attribute: "default-to-today" })
  defaultToToday = false;

  @property({ type: Boolean, attribute: "week-select" })
  weekSelect = false;

  @property({ type: Boolean, attribute: "month-select" })
  monthSelect = false;

  @property({ type: Boolean, attribute: "confirm-date" })
  confirmDate = false;

  _instance?: Instance;
  _inputElement?: HTMLInputElement;

  @property({ type: Boolean })
  _hasSlottedElement = false;

  static styles = css`
    :host {
      width: fit-content;
      display: block;
      cursor: pointer;
      background: #fff;
      color: #000;
      overflow: hidden;
    }
    ::slotted(*) {
      cursor: pointer;
    }
    ::placeholder {
      text-align: center;
    }
    input {
      text-align: center;
      width: 100%;
      height: 100%;
      font-size: inherit;
      cursor: pointer;
      background: inherit;
      box-sizing: border-box;
      outline: none;
      color: inherit;
      border: 1px solid;
      border-radius: 3px;
    }
  `;

  firstUpdated() {
    this._hasSlottedElement = this.checkForSlottedElement();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has("localeTh")) {
      if (this._instance) {
        this.clear();
      }
    }
    this.init();
  }

  getToday() {
    const today = new Date();
    return `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
  }

  getLocale() {
    const today = new Date();
    return `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
  }

  checkForSlottedElement(): boolean {
    const slottedElem = this.shadowRoot?.querySelector("slot");
    const assignedNodes = slottedElem ? slottedElem.assignedNodes().filter(this.removeTextNodes) : [];
    return slottedElem != null && assignedNodes && assignedNodes.length > 0;
  }

  getSlottedElement(): Element | undefined {
    if (!this._hasSlottedElement) {
      return undefined;
    }
    const slottedElem = this.shadowRoot?.querySelector("slot");
    const slottedElemNodes: Array<Node> | undefined = slottedElem?.assignedNodes().filter(this.removeTextNodes);
    if (!slottedElemNodes || slottedElemNodes.length < 1) {
      return undefined;
    }
    return slottedElemNodes[0] as Element;
  }

  removeTextNodes(node: Node): boolean {
    return node.nodeName !== "#text";
  }

  async init(): Promise<void> {
    const styleLoader = new StyleLoader(this.theme as FlatpickrTheme);
    await styleLoader.initStyles();
    await this.initializeComponent();
  }

  async getOptions(): Promise<Options> {
    let options = {
      altFormat: this.altFormat,
      altInput: this.altInput,
      altInputClass: this.altInputClass,
      allowInput: this.allowInput,
      ariaDateFormat: this.ariaDateFormat,
      clickOpens: this.clickOpens,
      dateFormat: this.dateFormat,
      defaultDate: this.defaultToToday ? this.getToday() : this.defaultDate,
      defaultHour: this.defaultHour,
      defaultMinute: this.defaultMinute,
      disable: this.disable,
      disableMobile: this.disableMobile,
      enable: this.enable,
      enableTime: this.enableTime,
      enableSeconds: this.enableSeconds,
      formatDate: this.formatDateFn,
      hourIncrement: this.hourIncrement,
      inline: this.inline,
      maxDate: this.maxDate,
      minDate: this.minDate,
      minuteIncrement: this.minuteIncrement,
      mode: this.mode,
      nextArrow: this.nextArrow,
      prevArrow: this.prevArrow,
      noCalendar: this.noCalendar,
      onChange: this.onChange,
      onClose: this.onClose,
      onOpen: this.onOpen,
      onReady: this.onReady,
      onMonthChange: this.onMonthChange,
      onYearChange: this.onYearChange,
      onValueUpdate: this.onValueUpdate,
      parseDate: this.parseDateFn,
      position: this.position,
      shorthandCurrentMonth: this.shorthandCurrentMonth,
      showMonths: this.showMonths,
      static: this.static,
      time_24hr: this.time_24hr,
      weekNumbers: this.weekNumbers,
      wrap: this.wrap,
      locale: this.localeTh ? Thai : english,
      plugins: [],
      useLocaleYear: true,
    } as any;
    options = await loadPlugins(this, options);
    Object.keys(options).forEach((key) => {
      if (options[key] === undefined) delete options[key];
    });
    return options;
  }

  async initializeComponent(): Promise<void> {
    if (this._instance) {
      if (Object.prototype.hasOwnProperty.call(this, "destroy")) {
        this._instance.destroy();
      }
    }

    let inputElement: HTMLInputElement | null;
    if (this._hasSlottedElement) {
      inputElement = this.findInputField();
    } else {
      inputElement = this.shadowRoot?.querySelector("input") as HTMLInputElement;
    }

    if (inputElement) {
      this._inputElement = inputElement;
      flatpickr.l10ns.default.firstDayOfWeek = this.firstDayOfWeek;
      const options = await this.getOptions();
      this._instance = flatpickr(inputElement, options);
    }
  }

  findInputField(): HTMLInputElement | null {
    let inputElement: HTMLInputElement | null = null;
    inputElement = this.querySelector("input");
    if (inputElement) {
      return inputElement;
    }
    const slottedElement: Element | undefined = this.getSlottedElement();
    if (typeof slottedElement !== undefined) {
      inputElement = this.searchWebComponentForInputElement(slottedElement as Element);
    }

    return inputElement ? inputElement : null;
  }

  searchWebComponentForInputElement(element: Element): HTMLInputElement | null {
    let inputElement: HTMLInputElement | null = this.getInputFieldInElement(element);
    if (inputElement) return inputElement;

    const webComponentsInChildren = this.getWebComponentsInsideElement(element);
    for (let i = 0; i < webComponentsInChildren.length; i++) {
      inputElement = this.searchWebComponentForInputElement(webComponentsInChildren[i]);
      if (inputElement) {
        break;
      }
    }
    return inputElement;
  }

  getInputFieldInElement(element: Element): HTMLInputElement | null {
    let inputElement: HTMLInputElement | null = null;
    if (element.shadowRoot) {
      inputElement = element.shadowRoot.querySelector("input");
    } else {
      inputElement = element.querySelector("input");
    }
    return inputElement;
  }

  getWebComponentsInsideElement(element: Element): Array<Element> {
    if (element.shadowRoot) {
      return [
        ...Array.from(element.querySelectorAll("*")),
        ...Array.from(element.shadowRoot.querySelectorAll("*")),
      ].filter((elem: Element) => elem.shadowRoot);
    } else {
      return Array.from(element.querySelectorAll("*")).filter((elem: Element) => elem.shadowRoot);
    }
  }

  changeMonth(monthNum: number, isOffset = true): void {
    if (!this._instance) return;
    this._instance.changeMonth(monthNum, isOffset);
  }

  clear(): void {
    if (!this._instance) return;
    this._instance.clear();
  }

  close(): void {
    if (!this._instance) return;
    this._instance.close();
  }

  destroy(): void {
    if (!this._instance) return;
    this._instance.destroy();
  }

  formatDate(dateObj: Date, formatStr: string): string {
    if (!this._instance) return "";
    return this._instance.formatDate(dateObj, formatStr);
  }

  jumpToDate(date: Date, triggerChange: boolean) {
    if (!this._instance) return;
    this._instance.jumpToDate(date, triggerChange);
  }

  open(): void {
    if (!this._instance) return;
    this._instance.open();
  }

  parseDate(dateStr: string, dateFormat: string): Date | undefined {
    if (!this._instance) return undefined;
    return this._instance.parseDate(dateStr, dateFormat);
  }

  redraw(): void {
    if (!this._instance) return;
    this._instance.redraw();
  }

  set(
    option:
      | keyof Options
      | {
          [k in keyof Options]?: Options[k];
        },
    value?: any
  ): void {
    if (!this._instance) return;
    this._instance.set(option, value);
  }

  setDate(date: DateOption | DateOption[], triggerChange: boolean, dateStrFormat: string): void {
    if (!this._instance) return;
    this._instance.setDate(date, triggerChange, dateStrFormat);
  }

  toggle(): void {
    if (!this._instance) return;
  }

  getSelectedDates(): Array<Date> {
    if (!this._instance) return [];
    return this._instance.selectedDates;
  }

  getCurrentYear(): number {
    if (!this._instance) return -1;
    return this._instance.currentYear;
  }

  getCurrentMonth(): number {
    if (!this._instance) return -1;
    return this._instance.currentMonth;
  }

  getConfig(): ParsedOptions {
    if (!this._instance) return {} as ParsedOptions;
    return this._instance.config;
  }

  getValue(): string {
    if (!this._inputElement) return "";
    return this._inputElement.value;
  }

  render() {
    return html`
      ${!this._hasSlottedElement
        ? html`<input class="lit-flatpickr flatpickr flatpickr-input" placeholder="Select Date" /> `
        : html``}
      <slot></slot>
    `;
  }
}
